import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ShoppingBag, Sparkles } from "lucide-react";
import { useHybridCart } from "@/contexts/HybridCartContext";
import { useToast } from "@/components/ui/use-toast";
import { useState, useMemo, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import productService from "@/services/productService";
import { pixelAddToCart } from "@/lib/pixel";
import { gtmEvent, mapItemToGA4 } from "@/lib/gtm";

interface OpcionTamano {
  id: string;
  nombre: string;
  dimensiones: {
    ancho: number;
    alto: number;
    unidad: string;
  };
  orientacion: "horizontal" | "vertical";
  precio: number;
}

interface ProductCardProps {
  id?: string | number;
  image: string;
  title: string;
  name?: string; // Some products use 'name' instead of 'title'
  price: number;
  type: "digital" | "physical" | "taller";
  stock?: number;
  category?: string;
  active?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onLoginRequired?: () => void;
  permiteNombrePersonalizado?: boolean; // Nombre personalizado simple sobre el producto
  esPersonalizable?: boolean; // Nuevo sistema de personalización
  precioPersonalizado?: number; // Precio premium para personalización
  slug?: string; // Slug para navegación a personalización
  orientacion?: 'horizontal' | 'vertical';
  opcionesTamano?: OpcionTamano[]; // Opciones de tamaño para personalización (si esPersonalizable)
  opcionesTamanoEstandar?: OpcionTamano[]; // Opciones de tamaño estándar (para botón "Comprar")
}

export const ProductCard = memo(({
  id,
  image,
  title,
  name,
  price,
  type,
  stock = 10, // Default stock
  category = "general",
  active = true,
  className,
  style,
  onLoginRequired,
  permiteNombrePersonalizado = false,
  esPersonalizable = false,
  precioPersonalizado,
  slug,
  orientacion,
  opcionesTamano,
  opcionesTamanoEstandar,
}: ProductCardProps) => {
  const { addToCart } = useHybridCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [personalizedName, setPersonalizedName] = useState("");

  // Prefetch del detalle al hacer hover para navegación instantánea
  const handlePrefetch = useCallback(() => {
    if (id) {
      queryClient.prefetchQuery({
        queryKey: ["product", String(id)],
        queryFn: () => productService.getProduct(String(id)),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [id, queryClient]);

  const productName = name || title;

  // Calcular precio y tamaño por defecto (memoizado para performance)
  const precioYTamanoDefault = useMemo(() => {
    if (opcionesTamanoEstandar && opcionesTamanoEstandar.length > 0) {
      const tamano = opcionesTamanoEstandar[0];
      return {
        precio: tamano.precio,
        tamano: tamano,
        nombre: tamano.nombre,
      };
    }
    return {
      precio: price,
      tamano: null,
      nombre: null,
    };
  }, [opcionesTamanoEstandar, price]);

  const handleViewProduct = () => {
    if (id) {
      navigate(`/products/${id}`);
    }
  };

  const handlePersonalizar = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!slug) {
      toast({
        title: "Error",
        description: "Este producto no tiene configuración de personalización",
        variant: "destructive",
      });
      return;
    }

    navigate(`/personalizar/${slug}`);
  };

  const handleAddToCart = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation(); // Evitar que se dispare el click de navegación

    if (!id) {
      toast({
        title: "Error",
        description: "Producto no disponible para agregar al carrito",
        variant: "destructive",
      });
      return;
    }

    if (!active) {
      toast({
        title: "Producto no disponible",
        description: "Este producto no está disponible actualmente",
        variant: "destructive",
      });
      return;
    }

    if (type === "physical" && stock <= 0) {
      toast({
        title: "Sin stock",
        description: "Este producto está agotado",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Validar que tenemos un ID válido
      if (!id) {
        console.error("ProductCard: No product ID available");
        throw new Error("ID de producto no disponible");
      }

      // Obtener precio y tamaño por defecto
      const { precio: precioFinal, tamano: tamanoInfo } = precioYTamanoDefault;

      console.log("ProductCard - Adding to cart:", {
        id: id,
        idType: typeof id,
        productName,
        precioFinal,
        tamanoInfo: tamanoInfo?.nombre,
      });

      // Create product object for hybrid cart
      const product = {
        _id: id.toString(),
        name: productName,
        price: precioFinal,
        image,
        images: [image],
        stock,
        category,
        active,
      };

      // Preparar personalización simple (solo nombre + tamaño si aplica)
      const customization: any = {};
      if (personalizedName.trim()) {
        customization.personalizedName = personalizedName.trim();
      }
      if (tamanoInfo) {
        customization.tamano = {
          id: tamanoInfo.id,
          nombre: tamanoInfo.nombre,
          dimensiones: `${tamanoInfo.dimensiones.ancho}cm × ${tamanoInfo.dimensiones.alto}cm`,
          orientacion: tamanoInfo.orientacion,
        };
      }

      console.log("ProductCard - Final product object:", product);
      console.log("ProductCard - Customization:", customization);

      await addToCart(
        product,
        1,
        Object.keys(customization).length > 0 ? customization : undefined
      );

      // Analytics: AddToCart (Meta) + add_to_cart (GA4) del quick-add
      pixelAddToCart({
        content_ids: [id.toString()],
        content_name: productName,
        value: precioFinal,
      });
      gtmEvent("add_to_cart", {
        ecommerce: {
          currency: "USD",
          value: precioFinal,
          items: [
            mapItemToGA4({
              id: id.toString(),
              name: productName,
              price: precioFinal,
              quantity: 1,
              type,
            }),
          ],
        },
      });

      // Limpiar el campo de personalización después de agregar
      setPersonalizedName("");

      // Toast is handled by the cart context
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast({
        title: "Error",
        description: "No se pudo añadir el producto al carrito",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "group cursor-pointer rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-2.5 sm:p-4 flex flex-col items-center relative overflow-hidden",
        // Destacar productos Premium con degradado de fondo y borde especial
        esPersonalizable
          ? "bg-gradient-to-br from-[#FFF8F0] via-[#F8F6F4] to-[#FFF0E6] border-2 border-purple-300/60 ring-2 ring-purple-200/40 hover:ring-purple-300/60 hover:shadow-purple-200/50"
          : "bg-[#F8F6F4] border border-primary/10",
        className
      )}
      style={style}
      onClick={handleViewProduct}
      onMouseEnter={handlePrefetch}
    >
      {/* Efecto de brillo para productos Premium */}
      {esPersonalizable && (
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-yellow-200/10 to-purple-200/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      )}
      <div className={cn(
        "relative overflow-hidden rounded-xl w-full mb-2 sm:mb-4 bg-gradient-to-br from-gray-50 to-gray-100 p-2 sm:p-3 flex items-center justify-center",
        orientacion === 'horizontal' ? 'aspect-[4/3]' : 'aspect-[4/5]'
      )}>
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
        />
        {/* Badge PREMIUM en esquina superior derecha */}
        {esPersonalizable && (
          <div className="absolute top-0 right-0">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xs px-4 py-2 rounded-bl-2xl shadow-lg flex items-center gap-1">
              <Sparkles className="h-4 w-4 animate-pulse" />
              Personalizable
            </div>
          </div>
        )}
        {/* Badge TALLER */}
        {type === "taller" && (
          <div className="absolute top-0 right-0">
            <div className="bg-amber-500 text-white font-bold text-xs px-4 py-2 rounded-bl-2xl shadow-lg">
              🎨 Taller
            </div>
          </div>
        )}

        {/* <div className="absolute top-3 left-3 flex flex-col gap-2">
          <span className="text-xs font-semibold bg-primary/90 text-brown-dark px-3 py-1 rounded-full shadow-sm uppercase tracking-wide animate-fade-in">
            {type === "digital" ? "Digital" : "Físico"}
          </span>
          {esPersonalizable && (
            <span className="text-xs font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full shadow-md uppercase tracking-wide animate-fade-in flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Personalizable
            </span>
          )}
        </div> */}

        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-background/90 to-transparent opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 transform translate-y-0 md:translate-y-2 md:group-hover:translate-y-0">
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            {/* Input de nombre personalizado */}
            {permiteNombrePersonalizado && (
              <Input
                type="text"
                placeholder="⭐ Nombre para personalizar"
                value={personalizedName}
                onChange={(e) => setPersonalizedName(e.target.value)}
                className="w-full text-sm bg-white/95 border-2 border-primary/40 focus:border-primary font-medium placeholder:font-normal"
                maxLength={50}
              />
            )}
            {/* Nuevo sistema: Producto personalizable con dos opciones */}
            {esPersonalizable ? (
              <>
                {/* Botón de compra directa - agrega al carrito con tamaño por defecto */}
                {/* <Button
                  onClick={handleAddToCart}
                  disabled={
                    loading || (type === "physical" && stock <= 0) || !active
                  }
                  variant="outline"
                  className="w-full bg-white/95 text-brown-dark hover:bg-white border-2 border-brown-light/30 hover:border-brown-light/50 gap-2 rounded-full font-medium shadow-sm py-2.5 text-sm transition-all duration-300"
                >
                  <ShoppingBag className="h-4 w-4" />
                  {loading ? "Añadiendo..." : `Comprar $${precioYTamanoDefault.precio.toFixed(2)}`}
                </Button> */}

                {/* Botón de personalización */}
                <Button
                  onClick={handlePersonalizar}
                  disabled={!active}
                  className="w-full bg-gradient-to-r from-primary to-primary/80 text-brown-dark hover:from-primary/90 hover:to-primary/70 gap-1 rounded-full font-semibold shadow-md py-2 sm:py-3 text-xs sm:text-base transition-all duration-300"
                >
                  ✨ Personaliza desde $
                  {opcionesTamano && opcionesTamano.length > 0
                    ? opcionesTamano[0].precio.toFixed(2)
                    : price.toFixed(2)}
                </Button>
              </>
            ) : type === "taller" ? (
              /* Botón para ver detalle del taller */
              <Button
                onClick={(e) => { e.stopPropagation(); handleViewProduct(); }}
                disabled={!active}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-1 rounded-full font-semibold shadow-md py-2 sm:py-3 text-xs sm:text-base transition-all duration-300"
              >
                🎨 Ver Taller
              </Button>
            ) : (
              /* Botón estándar para productos no personalizables */
              <Button
                onClick={handleAddToCart}
                disabled={
                  loading || (type === "physical" && stock <= 0) || !active
                }
                className="w-full bg-primary text-brown-dark hover:bg-primary-dark gap-1 rounded-full font-semibold shadow-md py-2 sm:py-3 text-xs sm:text-base transition-all duration-300"
              >
                <ShoppingBag className="h-3.5 w-3.5 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">
                  {loading
                    ? "Añadiendo..."
                    : type === "physical" && stock <= 0
                    ? "Sin stock"
                    : "Añadir al carrito"}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="w-full flex-1 flex flex-col justify-between relative z-10">
        <h3
          className={cn(
            "font-playfair text-sm sm:text-xl transition-colors duration-300 mb-1 sm:mb-2 text-center leading-tight",
            esPersonalizable
              ? "text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 font-bold group-hover:from-purple-700 group-hover:via-pink-700 group-hover:to-purple-700"
              : "text-brown-dark group-hover:text-primary"
          )}
        >
          {title}
        </h3>
        <div className="flex items-center justify-between w-full px-1 sm:px-2 mb-1 sm:mb-2">
          <p className="text-brown font-bold text-sm sm:text-lg">${price.toFixed(2)}</p>
          <div className="flex items-center space-x-0.5 sm:space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                className="w-3 h-3 sm:w-4 sm:h-4 text-brown"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
});
